import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import TipTapImage from '@tiptap/extension-image';
import TextAlign from '@tiptap/extension-text-align';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { DialogFooter } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Users, Loader2, Edit, Save, Send, Tag, X, Plus, CheckCircle } from 'lucide-react';
import { NewsletterAttachments } from './NewsletterAttachments';

export default function NewsletterComposer({ newsletter, availableTags, audiences = [], onSaveAudience, onOpenAudienceManager, onSave, onCancel }) {
  const [formData, setFormData] = useState({
    id: newsletter?.id || null,
    subject: newsletter?.subject || '',
    content: newsletter?.content || '',
    recipient_filter: newsletter?.recipient_filter || 'all',
    attachments: newsletter?.attachments || [],
  });
  const [isSaving, setIsSaving] = useState(false);
  const [recipientCount, setRecipientCount] = useState(null);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [savingAudience, setSavingAudience] = useState(false);
  const [newAudienceName, setNewAudienceName] = useState('');
  const [showSaveAudienceInput, setShowSaveAudienceInput] = useState(false);

  const token = localStorage.getItem('ams_admin_token');
  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  // Parse current filter into UI state: mode + selected tags
  const parseFilter = (f) => {
    if (!f) return { mode: 'all', tags: [] };
    if (f === 'all' || f === 'contacts' || f === 'subscribers') return { mode: f, tags: [] };
    if (f.startsWith('tags:')) {
      const tags = f.slice(5).split(',').map((t) => t.trim()).filter(Boolean);
      return { mode: 'tags', tags };
    }
    return { mode: 'tags', tags: [f] };
  };

  const { mode, tags: selectedTags } = parseFilter(formData.recipient_filter);

  const buildFilter = (m, tagsList) => {
    if (m === 'all' || m === 'contacts' || m === 'subscribers') return m;
    if (!tagsList.length) return 'tags:';
    return `tags:${tagsList.join(',')}`;
  };

  const setFilter = (m, tagsList = selectedTags) => {
    setFormData((prev) => ({ ...prev, recipient_filter: buildFilter(m, tagsList) }));
  };

  const toggleTag = (tag) => {
    const next = selectedTags.includes(tag)
      ? selectedTags.filter((t) => t !== tag)
      : [...selectedTags, tag];
    setFilter('tags', next);
  };

  const selectAudience = (audienceId) => {
    if (audienceId === '__custom__') {
      setFormData((prev) => ({ ...prev, recipient_filter: 'tags:' }));
      return;
    }
    const a = audiences.find((x) => x.id === audienceId);
    if (a) {
      setFormData((prev) => ({ ...prev, recipient_filter: a.filter }));
    }
  };

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const f = formData.recipient_filter;
      if (!f || f === 'tags:') {
        setRecipientCount(0);
        return;
      }
      setIsPreviewLoading(true);
      try {
        const res = await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/api/admin/filter/preview`,
          { filter: f },
          authHeaders
        );
        if (!cancelled) setRecipientCount(res.data.count);
      } catch (err) {
        if (!cancelled) setRecipientCount(null);
      } finally {
        if (!cancelled) setIsPreviewLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [formData.recipient_filter]);

  const matchingAudience = audiences.find((a) => a.filter === formData.recipient_filter);

  const handleSaveCurrentAsAudience = async () => {
    if (!newAudienceName.trim()) {
      toast.error('Please enter an audience name');
      return;
    }
    if (formData.recipient_filter === 'tags:') {
      toast.error('Please select at least one tag first');
      return;
    }
    setSavingAudience(true);
    const created = await onSaveAudience({
      name: newAudienceName.trim(),
      description: null,
      filter: formData.recipient_filter,
    });
    setSavingAudience(false);
    if (created) {
      setNewAudienceName('');
      setShowSaveAudienceInput(false);
    }
  };

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        link: { openOnClick: false },
      }),
      TipTapImage.configure({ inline: true }),
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
    ],
    content: newsletter?.content || '',
    onUpdate: ({ editor }) => {
      setFormData(prev => ({ ...prev, content: editor.getHTML() }));
    },
  });

  const addImage = useCallback(() => {
    const url = window.prompt('Enter image URL');
    if (url && editor) {
      editor.chain().focus().setImage({ src: url }).run();
    }
  }, [editor]);

  const setLink = useCallback(() => {
    const previousUrl = editor?.getAttributes('link').href;
    const url = window.prompt('URL', previousUrl);
    if (url === null) return;
    if (url === '') {
      editor?.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    editor?.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  }, [editor]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.subject.trim()) {
      toast.error('Subject is required');
      return;
    }
    if (!formData.content.trim() || formData.content === '<p></p>') {
      toast.error('Email content is required');
      return;
    }
    setIsSaving(true);
    await onSave(formData);
    setIsSaving(false);
  };

  if (!editor) {
    return <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Recipient Filter / Audience */}
      <div className="space-y-3 p-4 bg-muted/30 rounded-lg border" data-testid="newsletter-audience-section">
        <div className="flex items-center justify-between gap-2">
          <Label className="flex items-center gap-2">
            <Users className="w-4 h-4" />
            Audience
          </Label>
          <div className="flex items-center gap-2 text-sm">
            {isPreviewLoading ? (
              <Loader2 className="w-3 h-3 animate-spin text-muted-foreground" />
            ) : recipientCount !== null ? (
              <Badge variant="secondary" data-testid="newsletter-recipient-count">
                {recipientCount} recipient{recipientCount !== 1 ? 's' : ''}
              </Badge>
            ) : null}
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={onOpenAudienceManager}
              data-testid="newsletter-manage-audiences-btn"
            >
              <Edit className="w-3 h-3 mr-1" />
              Manage
            </Button>
          </div>
        </div>

        <Select
          value={matchingAudience ? matchingAudience.id : (mode === 'tags' ? '__custom__' : mode)}
          onValueChange={(value) => {
            if (value === 'all' || value === 'contacts' || value === 'subscribers') {
              setFilter(value);
            } else {
              selectAudience(value);
            }
          }}
        >
          <SelectTrigger data-testid="newsletter-recipient-filter">
            <SelectValue placeholder="Select an audience" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Contacts &amp; Subscribers</SelectItem>
            <SelectItem value="contacts">Contacts Only</SelectItem>
            <SelectItem value="subscribers">Newsletter Subscribers Only</SelectItem>
            {audiences.length > 0 && (
              <>
                <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground border-t mt-1">
                  Saved Audiences
                </div>
                {audiences.map((a) => (
                  <SelectItem key={a.id} value={a.id} data-testid={`audience-option-${a.id}`}>
                    <div className="flex items-center gap-2">
                      <Users className="w-3 h-3" />
                      {a.name}
                    </div>
                  </SelectItem>
                ))}
              </>
            )}
            <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground border-t mt-1">
              Custom
            </div>
            <SelectItem value="__custom__" data-testid="audience-option-custom">
              <div className="flex items-center gap-2">
                <Tag className="w-3 h-3" />
                Custom &mdash; pick tags
              </div>
            </SelectItem>
          </SelectContent>
        </Select>

        {mode === 'tags' && (
          <div className="space-y-2 pt-2 border-t" data-testid="audience-tag-picker">
            <Label className="text-xs text-muted-foreground">
              Include contacts with ANY of these tags:
            </Label>
            {availableTags.length === 0 ? (
              <p className="text-xs text-muted-foreground italic">
                No tags have been created yet. Add tags to contacts first.
              </p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {availableTags.map((tag) => {
                  const active = selectedTags.includes(tag);
                  return (
                    <Badge
                      key={tag}
                      variant={active ? 'default' : 'outline'}
                      className="cursor-pointer"
                      onClick={() => toggleTag(tag)}
                      data-testid={`audience-tag-chip-${tag}`}
                    >
                      {active && <CheckCircle className="w-3 h-3 mr-1" />}
                      <Tag className="w-3 h-3 mr-1" />
                      {tag}
                    </Badge>
                  );
                })}
              </div>
            )}

            {selectedTags.length > 0 && !matchingAudience && (
              <div className="pt-2">
                {showSaveAudienceInput ? (
                  <div className="flex items-center gap-2">
                    <Input
                      placeholder="Audience name (e.g. VIP + Vendors)"
                      value={newAudienceName}
                      onChange={(e) => setNewAudienceName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleSaveCurrentAsAudience();
                        }
                        if (e.key === 'Escape') {
                          setShowSaveAudienceInput(false);
                          setNewAudienceName('');
                        }
                      }}
                      autoFocus
                      data-testid="save-audience-name-input"
                    />
                    <Button
                      type="button"
                      size="sm"
                      onClick={handleSaveCurrentAsAudience}
                      disabled={savingAudience}
                      data-testid="save-audience-confirm-btn"
                    >
                      {savingAudience ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                      ) : (
                        <Save className="w-3 h-3" />
                      )}
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        setShowSaveAudienceInput(false);
                        setNewAudienceName('');
                      }}
                    >
                      <X className="w-3 h-3" />
                    </Button>
                  </div>
                ) : (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => setShowSaveAudienceInput(true)}
                    data-testid="save-as-audience-btn"
                  >
                    <Plus className="w-3 h-3 mr-1" />
                    Save as audience
                  </Button>
                )}
              </div>
            )}

            {matchingAudience && (
              <p className="text-xs text-muted-foreground italic pt-1">
                Matches saved audience: <strong>{matchingAudience.name}</strong>
              </p>
            )}
          </div>
        )}
      </div>

      {/* Subject */}
      <div className="space-y-2">
        <Label htmlFor="newsletter-subject">Subject Line *</Label>
        <Input
          id="newsletter-subject"
          placeholder="Enter your email subject..."
          value={formData.subject}
          onChange={(e) => setFormData(prev => ({ ...prev, subject: e.target.value }))}
          required
          data-testid="newsletter-subject"
        />
      </div>

      {/* Merge fields helper */}
      <div className="rounded-lg border border-dashed border-primary/30 bg-primary/5 p-3" data-testid="merge-fields-help">
        <p className="text-xs font-semibold text-primary uppercase tracking-wider mb-1.5">
          Merge fields (personalised per recipient)
        </p>
        <p className="text-xs text-muted-foreground mb-2">
          Use these tokens anywhere in your subject or body. Missing values fall back to friendly defaults (e.g. &ldquo;there&rdquo;).
        </p>
        <div className="flex flex-wrap gap-1.5">
          {['FIRST_NAME', 'LAST_NAME', 'FULL_NAME', 'BUSINESS_NAME', 'CRAFT_TYPE', 'EVENT'].map((f) => {
            const token = `{{${f}}}`;
            return (
              <button
                key={f}
                type="button"
                onClick={() => {
                  navigator.clipboard?.writeText(token);
                  toast.success(`Copied ${token}`);
                }}
                className="text-xs font-mono bg-white border border-primary/20 hover:border-primary text-primary px-2 py-1 rounded-full transition-colors"
                title={`Click to copy ${token}`}
                data-testid={`merge-field-${f.toLowerCase()}`}
              >
                {token}
              </button>
            );
          })}
        </div>
      </div>

      {/* Rich Text Editor */}
      <div className="space-y-2">
        <Label>Email Content *</Label>
        <div className="border rounded-lg overflow-hidden bg-white">
          <div className="flex flex-wrap gap-1 p-2 border-b bg-muted/30">
            <Button type="button" size="sm" variant={editor.isActive('bold') ? 'default' : 'ghost'} onClick={() => editor.chain().focus().toggleBold().run()} className="h-8 w-8 p-0">
              <strong>B</strong>
            </Button>
            <Button type="button" size="sm" variant={editor.isActive('italic') ? 'default' : 'ghost'} onClick={() => editor.chain().focus().toggleItalic().run()} className="h-8 w-8 p-0 italic">
              I
            </Button>
            <Button type="button" size="sm" variant={editor.isActive('underline') ? 'default' : 'ghost'} onClick={() => editor.chain().focus().toggleUnderline().run()} className="h-8 w-8 p-0 underline">
              U
            </Button>
            <Button type="button" size="sm" variant={editor.isActive('strike') ? 'default' : 'ghost'} onClick={() => editor.chain().focus().toggleStrike().run()} className="h-8 w-8 p-0 line-through">
              S
            </Button>
            <div className="w-px h-6 bg-border mx-1 self-center" />
            <Button type="button" size="sm" variant={editor.isActive('heading', { level: 1 }) ? 'default' : 'ghost'} onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} className="h-8 px-2">
              H1
            </Button>
            <Button type="button" size="sm" variant={editor.isActive('heading', { level: 2 }) ? 'default' : 'ghost'} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} className="h-8 px-2">
              H2
            </Button>
            <div className="w-px h-6 bg-border mx-1 self-center" />
            <Button type="button" size="sm" variant={editor.isActive('bulletList') ? 'default' : 'ghost'} onClick={() => editor.chain().focus().toggleBulletList().run()} className="h-8 px-2">
              • List
            </Button>
            <Button type="button" size="sm" variant={editor.isActive('orderedList') ? 'default' : 'ghost'} onClick={() => editor.chain().focus().toggleOrderedList().run()} className="h-8 px-2">
              1. List
            </Button>
            <div className="w-px h-6 bg-border mx-1 self-center" />
            <Button type="button" size="sm" variant={editor.isActive('link') ? 'default' : 'ghost'} onClick={setLink} className="h-8 px-2">
              Link
            </Button>
            <Button type="button" size="sm" variant="ghost" onClick={addImage} className="h-8 px-2">
              Image
            </Button>
          </div>

          <EditorContent
            editor={editor}
            className="prose prose-sm max-w-none p-4 min-h-[250px] focus:outline-none [&_.ProseMirror]:min-h-[200px] [&_.ProseMirror]:outline-none"
          />
        </div>
        <p className="text-xs text-muted-foreground">
          Use the toolbar to format text, add images, and create links
        </p>
      </div>

      <NewsletterAttachments
        attachments={formData.attachments}
        onChange={(attachments) => setFormData((prev) => ({ ...prev, attachments }))}
      />

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" disabled={isSaving} data-testid="newsletter-save-btn">
          {isSaving ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
              Saving...
            </>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              Save Draft
            </>
          )}
        </Button>
      </DialogFooter>
    </form>
  );
}
